import React, { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import useFetch from "../../../../hooks/useFetch";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSeedling,
  faHandHoldingUsd,
  faUser,
  faCheckCircle,
  faFileContract,
  faEye,
  faEnvelope,
  faPhone,
  faLightbulb,
  faStore,
  faStar,
} from "@fortawesome/free-solid-svg-icons";

import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";

// Central route constants — adjust here if your router paths differ from these.
const ROUTES = {
  INNOVATION_HUB: "/foundher/innovation-hub",
  BRANDS: "/foundher/brands",
  SPOTLIGHT: "/foundher/spotlight",
};

/**
 * Reads the "she has innovations / private brand / was spotlighted" flags off
 * a founder record. Backend field names aren't confirmed yet, so this checks
 * a few likely variants defensively. Once the real API contract is known,
 * trim this down to the actual field(s) — keeping it in one helper means
 * that's a one-place fix.
 */
const getFoundHerFlags = (founder, details) => {
  const profile = details?.profile || {};
  const source = { ...founder, ...profile };

  const hasInnovationHub = Boolean(
    source.hasInnovationHub ??
    source.hasInnovations ??
    source.isInnovationOnly ??
    source.innovationStoreCode,
  );

  const hasBrand = Boolean(
    source.hasBrandStore ??
    source.hasPrivateBrand ??
    source.isBrandOnly ??
    source.brandStoreCode,
  );

  const isSpotlighted = Boolean(
    source.isSpotlighted ??
    source.inSpotlight ??
    source.spotlightFeatured ??
    source.spotlightId,
  );

  return { hasInnovationHub, hasBrand, isSpotlighted };
};

const fallbackFounders = [
  {
    id: 1,
    name: "Priya Sharma",
    location: "Pune, India",
    need: "Prototype & Branding",
    goal: 30000,
    raised: 12500,
  },
];

const sampleFounder = {
  _id: "sample001",

  userId: "sample001",
  userCode: "T5W-FH-1001",
  userName: "Priya Sharma",

  profilePhoto: "",
  coverPhoto: "",

  missionTagline: "Building eco-friendly handmade lifestyle products.",

  headline: "Every successful founder had someone who believed in her.",

  supportStory:
    "I am building a women-led sustainable products brand. Your support can help me purchase equipment, create my first production batch and reach my first 100 customers.",

  location: "Pune, Maharashtra",

  currentStage: "Prototype",

  currentNeed: "Product Launch",

  fundraisingGoal: 30000,

  raisedAmount: 12500,

  supportNeeds: [
    "Money",
    "PreOrder",
    "Furniture",
    "Fixtures",
    "Machinery",
    "Rental",
    "Professional",
    "Mentorship",
    "MarketAccess",
    "Volunteer",
    "Other",
  ],

  gratitudeWall: [
    {
      supporterName: "ABC Women's Association",
      supportType: "Money",
      supportItem: "Seed Support",
    },
    {
      supporterName: "XYZ College Women Cell",
      supportType: "Mentorship",
      supportItem: "Business Mentoring",
    },
  ],

  collegeBadge: {
    institutionName: "XYZ College Women Cell",
    badgeTitle: "Verified Young Founder",
  },
};

const SupportAFoundHer = () => {
  const navigate = useNavigate();
  const authState = useSelector((state) => state.authReducer);
  const [fetchData] = useFetch();
  const userId = authState?.user?._id;
  const userCode = authState?.user?.think5whyscode;

  const token = authState?.token;
  const hasFetchedRef = useRef(false);

  const [founders, setFounders] = useState([]);
  const useSample = founders.length === 0;

  // const [showSample, setShowSample] = useState(false);
  const [loading, setLoading] = useState(false);

  const [donationAmount, setDonationAmount] = useState("");
  const [selectedFounder, setSelectedFounder] = useState(null);
  const [agree, setAgree] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");

  const [founderDetails, setFounderDetails] = useState(null);

  const [contactType, setContactType] = useState("");
  const [showContactModal, setShowContactModal] = useState(false);
  const [agreed, setAgreed] = useState(false);

  const supportTypes = [
    "Money",
    "PreOrder",
    "Furniture",
    "Fixtures",
    "Machinery",
    "Rental",
    "Professional",
    "Mentorship",
    "MarketAccess",
    "Volunteer",
    "Other",
  ];

  const [supporterForm, setSupporterForm] = useState({
    supporterCode: authState.user.think5whyscode,
    supporterName: authState.user.name,
    supportType: "Money",
    supportItem: "",
  });

  const fetchSupportProfiles = async () => {
    if (hasFetchedRef.current) return;

    try {
      setLoading(true);

      const config = {
        url: "/foundher/support-profile/list",
        method: "post",
        data: { founderId: userId },
        headers: { Authorization: token },
      };

      const response = await fetchData(config, {
        silent: true,
      });
      if (response?.status) {
        if (response?.records?.length) {
          setFounders(response.records);
        } else {
          setFounders([]);
        }
        hasFetchedRef.current = true;
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userId) {
      fetchSupportProfiles();
    }
  }, [userId]);

  const handleDonate = (founder) => {
    if (!donationAmount || !agree) {
      setSuccessMsg(
        "⚠️ Please enter an amount and agree to the Terms & Conditions.",
      );
      return;
    }

    setSelectedFounder(founder);

    setSuccessMsg(
      `💖 Thank you! Your support of ₹${donationAmount} has been pledged to ${founder.name || founder.userName}.`,
    );

    setDonationAmount("");
    setAgree(false);
  };

  const detailsRequestRef = useRef(0);

  const fetchFounderDetails = async (founder) => {
    const requestId = ++detailsRequestRef.current;
    setFounderDetails(null);

    const isSample = founder === sampleFounder;
    if (isSample) {
      setFounderDetails({
        status: true,
        profile: founder,
        supporters: [],
        badge: founder.collegeBadge || null,
      });
      return;
    }

    const config = {
      url: "/foundher/support-founder/details",
      method: "post",
      headers: { Authorization: token },
      data: { founderId: founder.userId },
    };

    const response = await fetchData(config, { silent: true });
    if (requestId !== detailsRequestRef.current) return;
    if (response?.status) {
      setFounderDetails(response);
    }
  };

  const handleSupporterChange = (e) => {
    let { name, value } = e.target;

    if (name === "supportItem" && supporterForm.supportType === "Money") {
      value = value.replace(/\D/g, "");
    }

    setSupporterForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  return (
    <div className="max-w-5xl mx-auto p-6 bg-black text-white min-h-screen">
      <h1 className="flex flex-col text-lg lg:text-2xl font-bold bg-pink-700 px-4 py-2 rounded-lg text-pink-100 items-center gap-2 mb-2">
        <FontAwesomeIcon icon={faSeedling} /> Support A FoundHer
        <p className="text-cyan-200 text-xs lg:text-sm">
          Every successful founder had someone who believed in her.
        </p>
      </h1>
      <p className="text-gray-100 mb-2 text-xs lg:text-sm">
        Help aspiring women founders turn their ideas into sustainable
        businesses. Your contribution can support essentials like product
        development, branding, business registration, equipment, mentoring, or
        launching their first sales. Every contribution helps a dream move one
        step closer to reality.
      </p>

      <h1 className="text-xs lg:text-sm font-bold bg-gray-700 px-4 py-2 rounded-lg text-pink-100 flex items-center gap-2 mb-2">
        The Following List may include a Fallback List for example purpose!
      </h1>

      {useSample && (
        <div className="mb-3 rounded-lg bg-yellow-700 p-3 text-xs text-yellow-100">
          No FoundHer profiles available yet. Showing sample data for
          demonstration.
        </div>
      )}

      {/* Founder Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {[sampleFounder, ...founders].map((founder, index) => {
          const goal = Number(founder.goal ?? founder.fundraisingGoal ?? 0);
          const raised = Number(founder.raised ?? founder.raisedAmount ?? 0);

          const percentage =
            goal > 0 ? Math.min(100, Math.round((raised * 100) / goal)) : 0;

          const flags = getFoundHerFlags(founder, null);
          const hasAnyFlag =
            flags.hasInnovationHub || flags.hasBrand || flags.isSpotlighted;

          return (
            <div
              key={founder._id || founder.id || index}
              className={`${founder === sampleFounder ? "bg-red-200" : "bg-white"} p-4 rounded-xl shadow hover:shadow-lg transition`}
            >
              <div className="flex items-center gap-3 mb-1">
                <div className="relative w-12 h-12 shrink-0">
                  {founder.profilePhoto ? (
                    <img
                      src={`/api/downloads/users/foundherprofilepic/${founder.profilePhoto}`}
                      alt={founder.name || founder.userName}
                      className="w-10 h-10 rounded-full object-cover border-2 border-pink-200"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-pink-100 border-2 border-pink-200 flex items-center justify-center text-xl">
                      👩‍💼
                    </div>
                  )}
                  {founder.collegeVerified && (
                    <span
                      title="Verified Founder"
                      className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-blue-500 border-2 border-white flex items-center justify-center text-white"
                    >
                      <FontAwesomeIcon
                        icon={faCheckCircle}
                        className="text-[10px]"
                      />
                    </span>
                  )}
                </div>
                {founder.collegeVerified && founder.collegeCode && (
                  <img
                    src={`/api/downloads/users/foundherbadges/${founder.collegeCode}.jpg`}
                    alt=""
                    className="w-10 h-10 rounded-lg object-cover border border-gray-200 shrink-0"
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />
                )}
                <div>
                  <h2 className="text-lg font-semibold text-gray-800">
                    {founder.name || founder.userName}
                  </h2>
                  {founder.collegeVerified && (
                    <span className="text-[11px] font-medium text-blue-600">
                      ✓ Verified Founder
                    </span>
                  )}
                </div>
              </div>
              <p className="text-sm text-gray-500">{founder.location}</p>
              <p className="mt-2 text-gray-700">
                <strong>Need:</strong> {founder.need || founder.currentNeed}
              </p>
              <p className="text-gray-600 text-sm">
                Goal: ₹
                {Number(
                  founder.goal ?? founder.fundraisingGoal ?? 0,
                ).toLocaleString()}{" "}
                | Raised: ₹
                {Number(
                  founder.raised ?? founder.raisedAmount ?? 0,
                ).toLocaleString()}
              </p>

              {/* Progress bar */}
              <div className="w-full bg-gray-200 rounded-full h-3 mt-2">
                <div
                  className="bg-green-600 h-3 rounded-full"
                  style={{ width: `${percentage}%` }}
                ></div>
              </div>
              <p className="text-xs text-gray-500 mt-1">{percentage}% funded</p>

              {/* FoundHer flags — only shown when the DB indicates she has an
                  Innovation Hub listing, a private brand, or was Spotlighted */}
              {hasAnyFlag && (
                <div className="flex flex-wrap gap-1 mt-3">
                  {flags.hasInnovationHub && (
                    <button
                      onClick={() => navigate(ROUTES.INNOVATION_HUB)}
                      className="flex items-center gap-1 text-xs font-medium bg-violet-100 text-violet-700 hover:bg-violet-200 w-fit px-2 py-1 rounded-full transition"
                    >
                      <FontAwesomeIcon icon={faLightbulb} /> Has Innovations
                    </button>
                  )}
                  {flags.hasBrand && (
                    <button
                      onClick={() => navigate(ROUTES.BRANDS)}
                      className="flex items-center gap-1 text-xs font-medium bg-pink-100 text-pink-700 hover:bg-pink-200 w-fit px-2 py-1 rounded-full transition"
                    >
                      <FontAwesomeIcon icon={faStore} /> Has a Brand
                    </button>
                  )}
                  {flags.isSpotlighted && (
                    <button
                      onClick={() => navigate(ROUTES.SPOTLIGHT)}
                      className="flex items-center gap-1 text-xs font-medium bg-amber-100 text-amber-700 hover:bg-amber-200 w-fit px-2 py-1 rounded-full transition"
                    >
                      <FontAwesomeIcon icon={faStar} /> In Spotlight
                    </button>
                  )}
                </div>
              )}

              {/* Donation Input */}
              <div className="mt-4">
                <div className="flex flex-row gap-1 mt-3">
                  <div className="flex flex-row items-center gap-2 rounded-md border border-gray-200 px-2 py-2">
                    <div className="text-xs font-medium text-gray-900">
                      To Contribute
                    </div>
                    <div className="flex felx-row gap-2">
                      <button
                        onClick={() => {
                          setSelectedFounder(founder);
                          setContactType("whatsapp");
                          setShowContactModal(true);
                        }}
                        className="w-8 h-8 rounded-full bg-green-600 text-white hover:bg-green-700"
                      >
                        <FontAwesomeIcon icon={faWhatsapp} />
                      </button>

                      <button
                        onClick={() => {
                          setSelectedFounder(founder);
                          setContactType("email");
                          setShowContactModal(true);
                        }}
                        className="w-8 h-8 rounded-full bg-blue-600 text-white hover:bg-blue-700"
                      >
                        <FontAwesomeIcon icon={faEnvelope} />
                      </button>

                      <button
                        onClick={() => {
                          setSelectedFounder(founder);
                          setContactType("call");
                          setShowContactModal(true);
                        }}
                        className="w-8 h-8 rounded-full bg-purple-600 text-white hover:bg-purple-700"
                      >
                        <FontAwesomeIcon icon={faPhone} />
                      </button>
                    </div>
                  </div>
                </div>

                <div className="flex px-1 py-1">
                  <button
                    onClick={() => {
                      setSelectedFounder(founder);
                      fetchFounderDetails(founder);
                    }}
                    className="bg-pink-700 hover:bg-pink-800 text-white text-xs lg:text-sm w-fit px-2 py-1 rounded-lg flex items-center justify-center gap-1"
                  >
                    <div className="flex flex-col justify-center">
                      <span className="text-xs lg:text-sm">
                        <FontAwesomeIcon icon={faEye} />
                        Supports She Got
                      </span>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {showContactModal && selectedFounder && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="bg-zinc-700 text-white rounded-xl w-[92%] max-w-md p-6">
            <h3 className="text-lg font-bold mb-3 bg-cyan-200">
              Contact Founder
            </h3>

            <p className="text-sm text-gray-100">You are about to contact</p>

            <p className="font-semibold mb-4 text-gray-100">
              {selectedFounder.userName}
            </p>

            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                checked={agreed}
                onChange={() => setAgreed(!agreed)}
              />

              <span>I agree to the Terms & Conditions.</span>
            </label>

            <div className="flex gap-2 mt-5">
              <button
                onClick={() => {
                  setShowContactModal(false);
                  setAgreed(false);
                }}
                className="flex-1 bg-gray-500 text-white rounded-lg py-2"
              >
                Cancel
              </button>

              <button
                disabled={!agreed}
                onClick={() => {
                  if (contactType === "whatsapp") {
                    window.open(
                      `https://wa.me/${selectedFounder.whatsapp}`,
                      "_blank",
                    );
                  } else if (contactType === "email") {
                    window.location.href = `mailto:${selectedFounder.email}`;
                  } else {
                    window.location.href = `tel:${selectedFounder.mobile}`;
                  }

                  setShowContactModal(false);
                  setAgreed(false);
                }}
                className={`
            flex-1 rounded-lg py-2 text-white
            ${agreed ? "bg-pink-700" : "bg-gray-400 cursor-not-allowed"}
          `}
              >
                Proceed
              </button>
            </div>
          </div>
        </div>
      )}

      {selectedFounder && (
        <div className="mt-6 bg-white rounded-xl p-5 text-gray-800 shadow">
          <h2 className="text-xl font-bold text-pink-700">
            {selectedFounder?.userName || sampleFounder.userName}
          </h2>

          <p className="text-sm text-gray-500">
            {selectedFounder?.missionTagline || sampleFounder.missionTagline}
          </p>

          <p className="mt-4">
            {selectedFounder?.supportStory || sampleFounder.supportStory}
          </p>
          {(() => {
            const flags = getFoundHerFlags(selectedFounder, founderDetails);
            const hasAnyFlag =
              flags.hasInnovationHub || flags.hasBrand || flags.isSpotlighted;

            if (!hasAnyFlag) return null;

            return (
              <div className="mt-5 bg-violet-50 border border-violet-200 rounded-lg p-4">
                <h3 className="font-semibold mb-3 text-violet-900">
                  ✨ More to Discover About Her
                </h3>
                <div className="flex flex-col sm:flex-row gap-3">
                  {flags.hasInnovationHub && (
                    <button
                      onClick={() => navigate(ROUTES.INNOVATION_HUB)}
                      className="flex-1 flex items-center justify-center gap-2 bg-violet-600 hover:bg-violet-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition"
                    >
                      <FontAwesomeIcon icon={faLightbulb} />
                      She Has Innovations
                    </button>
                  )}
                  {flags.hasBrand && (
                    <button
                      onClick={() => navigate(ROUTES.BRANDS)}
                      className="flex-1 flex items-center justify-center gap-2 bg-pink-600 hover:bg-pink-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition"
                    >
                      <FontAwesomeIcon icon={faStore} />
                      She Has a Private Brand
                    </button>
                  )}
                  {flags.isSpotlighted && (
                    <button
                      onClick={() => navigate(ROUTES.SPOTLIGHT)}
                      className="flex-1 flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-600 text-white text-sm font-medium px-4 py-2 rounded-lg transition"
                    >
                      <FontAwesomeIcon icon={faStar} />
                      She Was In the Spotlight
                    </button>
                  )}
                </div>
              </div>
            );
          })()}

          <div className="mt-5">
            <h3 className="font-semibold mb-2">Looking For Support</h3>

            <div className="flex flex-wrap gap-2">
              {(founderDetails?.profile?.supportNeeds?.length
                ? founderDetails.profile.supportNeeds
                : sampleFounder.supportNeeds
              ).map((item) => (
                <span
                  key={item.type || item}
                  className="bg-pink-100 text-pink-700 px-3 py-1 rounded-full text-xs"
                >
                  {item.type || item}
                </span>
              ))}
            </div>
          </div>

          <div className="mt-6">
            <h3 className="font-semibold mb-2">Gratitude Wall ❤️</h3>

            {founderDetails?.supporters?.length ? (
              founderDetails.supporters.map((item, index) => (
                <div key={index} className="border rounded-lg p-3 mb-2">
                  <div className="font-semibold">
                    {item.anonymous
                      ? "Anonymous Supporter ❤️"
                      : item.supporterName}
                  </div>

                  <div className="text-sm text-gray-700">
                    {item.supportType}
                  </div>

                  <div className="text-xs text-gray-500">
                    {item.supportItem}
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-6 text-gray-500 italic">
                💝 <b>Be the first supporter.</b>
                <br />
                Your encouragement can inspire this FoundHer journey.
              </div>
            )}
          </div>

          <div className="mt-6 bg-gradient-to-br from-pink-50 to-violet-50 border border-pink-200 rounded-2xl p-5">
            {founderDetails?.profile?.collegeVerified ? (
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-2xl bg-white border-2 border-pink-300 flex items-center justify-center overflow-hidden shrink-0">
                  {founderDetails?.badge?.institutionCode ||
                  founderDetails?.badge?.logo ? (
                    <img
                      src={
                        founderDetails.badge.logo
                          ? founderDetails.badge.logo
                          : `/api/downloads/users/foundherbadges/${founderDetails.badge.institutionCode}.jpg`
                      }
                      alt={founderDetails.badge.institutionName}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.style.display = "none";
                        e.target.nextSibling.style.display = "flex";
                      }}
                    />
                  ) : null}
                  <span
                    className="text-3xl"
                    style={{
                      display:
                        founderDetails?.badge?.institutionCode ||
                        founderDetails?.badge?.logo
                          ? "none"
                          : "flex",
                    }}
                  >
                    🎓
                  </span>
                </div>
                <div>
                  <span
                    className="inline-block px-3 py-1 rounded-full text-xs font-semibold text-white"
                    style={{
                      backgroundColor:
                        founderDetails?.badge?.badgeColor || "#EC4899",
                    }}
                  >
                    ✓ {founderDetails?.badge?.badgeTitle || "Verified Founder"}
                  </span>
                  <p className="font-semibold text-violet-950 mt-1.5">
                    {founderDetails?.profile?.collegeName ||
                      founderDetails?.badge?.institutionName}
                  </p>
                  {founderDetails?.badge?.verifiedOn && (
                    <p className="text-xs text-gray-500 mt-0.5">
                      Verified on{" "}
                      {new Date(
                        founderDetails.badge.verifiedOn,
                      ).toLocaleDateString()}
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-2xl bg-white border-2 border-dashed border-gray-300 flex items-center justify-center shrink-0">
                  <span className="text-3xl">👩‍🎓</span>
                </div>
                <div>
                  <p className="font-semibold text-gray-500">
                    🎓 Verification Pending
                  </p>
                  <p className="text-sm text-gray-500 mt-1">
                    This founder has not yet been verified by a College,
                    University, NGO, Women Cell or Incubation Centre.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Success message */}
      {successMsg && (
        <div className="mt-6 p-4 bg-pink-100 border border-pink-300 rounded-lg text-pink-800 flex items-center gap-2">
          <FontAwesomeIcon icon={faCheckCircle} /> {successMsg}
        </div>
      )}

      {/* Terms & Conditions Section */}
      <div className="mt-4 mb-4 bg-white shadow p-6 rounded-xl">
        <h3 className="text-xl font-semibold text-gray-800 mb-3 flex items-center gap-2">
          <FontAwesomeIcon icon={faFileContract} /> Terms & Conditions
        </h3>
        <ul className="list-disc pl-5 text-sm text-gray-600 space-y-2">
          <li className="text-gray-700 text-xs lg:text-sm">
            Contributions are voluntary and non-refundable. Think5Whys only
            facilitates connections between supporters and women entrepreneurs.
          </li>

          <li className="text-gray-700 text-xs lg:text-sm">
            Funds are intended to help with genuine entrepreneurial needs such
            as product development, branding, equipment, registration,
            marketing, or business growth.
          </li>

          <li className="text-gray-700 text-xs lg:text-sm">
            Supporters may receive updates on progress whenever founders choose
            to share them through the platform.
          </li>
        </ul>
      </div>
      {/* Footer */}
      <footer className="bg-pink-700 text-white py-10 px-4 sm:px-6 md:px-20">
        <div className="flex flex-col md:flex-row justify-between items-center text-center md:text-left">
          <p className="text-sm text-gray-100">
            &copy; 2026 Think5Whys Global DeepTech Solutions Private Limited.
            All rights reserved.
          </p>
          <div className="flex space-x-6 mt-4 md:mt-0 text-sm">
            <Link
              to="/about-us"
              className="text-white hover:text-green-400 transition"
            >
              About
            </Link>
            <Link
              to="/privacy-policy"
              className="text-white hover:text-green-400 transition"
            >
              Privacy Policy
            </Link>
            <Link
              to="/terms-of-use"
              className="text-white hover:text-green-400 transition"
            >
              Terms of Use
            </Link>
            <Link
              to="/contact-us"
              className="text-white hover:text-green-400 transition"
            >
              Contact
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SupportAFoundHer;
